import { ChatMultiSelect, ChatSuggestion, ChatSuggestions } from '@wundo/ui';
import { translate } from '@wundo/shared/labels.ts';
import { getButtons, hasButtons } from '@/utils/messageUtils.ts';
import type { Message } from '@/types/messages.ts';

export interface MessageChoicesProps {
  message: Message;
  /** Says a choice as the user. Absent — a transcript being read — leaves them inert. */
  onButtonClick?: (title: string) => void;
  /** Whether there is still anything to answer here. */
  answered?: boolean;
}

/**
 * What the message asked the user to pick from.
 *
 * A question that takes one answer says it on the click; one that takes several
 * holds the picks until they are confirmed, and then says all of them in a
 * single message — the same joining Teams does, which is the only other channel
 * that can show a multi-select at all.
 */
export function MessageChoices({
  message,
  onButtonClick,
  answered = true,
}: MessageChoicesProps) {
  if (!hasButtons(message)) return null;

  const buttons = getButtons(message);
  if (buttons.length === 0) return null;

  const options = buttons.map((button) => ({
    id: button.id ?? button.title,
    title: button.title,
  }));
  const multiple =
    message.body.type === 'template' && message.body.template.allow_multiple === true;
  const disabled = !onButtonClick || answered;

  if (multiple) {
    return (
      <ChatMultiSelect
        className='mt-3'
        label='Options'
        options={options}
        confirmLabel={translate('confirm', message.language ?? 'en')}
        onConfirm={(titles) => onButtonClick?.(titles.join(', '))}
        disabled={disabled}
      />
    );
  }

  return (
    <ChatSuggestions className='mt-3' label='Options'>
      {options.map((option) => (
        <ChatSuggestion
          key={option.id}
          text={option.title}
          disabled={disabled}
          onClick={() => onButtonClick?.(option.title)}
        />
      ))}
    </ChatSuggestions>
  );
}
